import type { SupabaseClient } from '@supabase/supabase-js';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';

import { signInWithGoogleNative } from '@/lib/auth/googleNative';
import { requireSupabase } from '@/lib/supabase/client';
import type { Database } from '@/lib/supabase/types';

/** Auth error → translation key under `auth.errors.*`. */
function authErrorKey(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('invalid login credentials')) return 'auth.errors.invalidCredentials';
  if (lower.includes('already registered') || lower.includes('already been registered')) {
    return 'auth.errors.emailTaken';
  }
  if (lower.includes('email not confirmed')) return 'auth.errors.emailNotConfirmed';
  if (lower.includes('valid email')) return 'auth.errors.invalidEmail';
  if (lower.includes('password')) return 'auth.errors.shortPassword';
  return 'auth.errors.generic';
}

export class AuthActionError extends Error {
  readonly translationKey: string;
  constructor(translationKey: string, message: string) {
    super(message);
    this.name = 'AuthActionError';
    this.translationKey = translationKey;
  }
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.auth.signInWithPassword({ email, password });
  if (error) throw new AuthActionError(authErrorKey(error.message), error.message);
}

export async function signUpWithPassword(
  email: string,
  password: string,
  displayName: string,
  locale: 'ar' | 'en',
): Promise<{ needsEmailConfirmation: boolean }> {
  const db = requireSupabase();
  const { data, error } = await db.auth.signUp({
    email,
    password,
    options: { data: { display_name: displayName, locale } },
  });
  if (error) throw new AuthActionError(authErrorKey(error.message), error.message);
  return { needsEmailConfirmation: data.session === null };
}

export async function signOut(): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.auth.signOut();
  if (error) throw new AuthActionError('auth.errors.generic', error.message);
}

function oauthErrorKey(message: string): string {
  if (/provider is not enabled|unsupported provider/i.test(message)) {
    return 'errors.OAUTH_DISABLED';
  }
  return 'auth.errors.generic';
}

/**
 * Complete the native OAuth redirect. Handles BOTH Supabase flows so it works
 * regardless of the client's flowType:
 *  - PKCE  → the URL carries `?code=...`; exchange it for a session.
 *  - implicit → the URL carries `#access_token=...&refresh_token=...`; set it.
 */
async function createSessionFromUrl(db: SupabaseClient<Database>, url: string): Promise<void> {
  const queryPart = url.includes('?') ? url.split('?')[1].split('#')[0] : '';
  const fragmentPart = url.includes('#') ? url.split('#')[1] : '';
  const query = new URLSearchParams(queryPart);
  const fragment = new URLSearchParams(fragmentPart);

  const errorDescription = query.get('error_description') ?? fragment.get('error_description');
  if (errorDescription) {
    throw new AuthActionError(oauthErrorKey(errorDescription), errorDescription);
  }

  const code = query.get('code');
  if (code) {
    const { error } = await db.auth.exchangeCodeForSession(code);
    if (error) throw new AuthActionError('auth.errors.generic', error.message);
    return;
  }

  const accessToken = fragment.get('access_token');
  const refreshToken = fragment.get('refresh_token');
  if (accessToken && refreshToken) {
    const { error } = await db.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) throw new AuthActionError('auth.errors.generic', error.message);
    return;
  }

  throw new AuthActionError('auth.errors.generic', 'OAuth redirect missing code/tokens');
}

export type OAuthProvider = 'google' | 'apple' | 'facebook';

/**
 * Social sign-in for any enabled Supabase provider.
 * Web: full-page redirect handled by Supabase. Native: an in-app browser
 * session that redirects back into the app (tashkeela:// deep link).
 * A provider that isn't enabled in Supabase yet surfaces OAUTH_DISABLED so the
 * UI can tell the user to use another method instead of failing silently.
 *
 * Provider status:
 *  - google   : enabled (Tashkeela Google Cloud OAuth client).
 *  - facebook : free; enable by adding a Meta app to Supabase Auth.
 *  - apple    : requires a paid Apple Developer account (Services ID + key).
 */
export async function signInWithOAuth(provider: OAuthProvider): Promise<void> {
  const db = requireSupabase();

  if (Platform.OS === 'web') {
    const { error } = await db.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
      },
    });
    if (error) throw new AuthActionError(oauthErrorKey(error.message), error.message);
    return; // the browser navigates away
  }

  // Native Google: sign in through the OS account picker and exchange the id
  // token, so the user never sees the in-app browser consent on the supabase.co
  // domain. Falls back to the web-redirect flow below when not configured (no
  // web client id), on a device without Play Services, or on any error.
  if (provider === 'google') {
    const native = await signInWithGoogleNative();
    if (native === 'cancelled') return; // user backed out of the picker
    if (native !== 'unavailable') {
      const { error } = await db.auth.signInWithIdToken({
        provider: 'google',
        token: native.idToken,
      });
      if (error) throw new AuthActionError(oauthErrorKey(error.message), error.message);
      return;
    }
  }

  const redirectTo = Linking.createURL('auth/callback');
  const { data, error } = await db.auth.signInWithOAuth({
    provider,
    options: { redirectTo, skipBrowserRedirect: true },
  });
  if (error || !data.url) {
    throw new AuthActionError(
      oauthErrorKey(error?.message ?? ''),
      error?.message ?? 'No OAuth URL returned',
    );
  }
  const result = await WebBrowser.openAuthSessionAsync(data.url, redirectTo);
  if (result.type === 'success' && result.url) {
    await createSessionFromUrl(db, result.url);
  }
}

/** Convenience wrapper kept for existing call sites. */
export function signInWithGoogle(): Promise<void> {
  return signInWithOAuth('google');
}
